import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiParcelsApprovePost } from '@/entities/parcels';
import type { ApiParcelsApprovePostResponse, ApiParcelsGetItem } from '@/shared/types/api/parcels';

type Props = {
  parcel: ApiParcelsGetItem | null;
  onClose: () => void;
};

export const FeaturesParcelsApproveDialog = ({ parcel, onClose }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [needsConfirm, setNeedsConfirm] = useState<ApiParcelsApprovePostResponse | null>(null);

  const approve = useMutate({
    api: ({ id, confirm }: { id: string; confirm: boolean }) =>
      apiParcelsApprovePost(id, { confirm }),
    invalidateKeys: [queryKeys.parcels.all],
    onSuccess: (data, vars) => {
      // First step: duplicate trek_id detected — surface the count and ask the
      // staff to confirm before re-calling with `confirm: true`.
      if (data.needs_confirmation && !vars.confirm) {
        setNeedsConfirm(data);
        return;
      }
      // Success path: the backend echoes the approved parcel under
      // `data.parcel` (present on both direct-approve and post-confirm 200s).
      if (data.parcel) {
        toast.success(t('parcels.toast.approved'));
        setNeedsConfirm(null);
        onClose();
      }
    },
    onError: (_e, message) => toast.error(message),
  });

  if (!parcel) return null;

  const isOpen = Boolean(parcel);

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        setNeedsConfirm(null);
        onClose();
      }}
      title={needsConfirm ? t('parcels.approve.duplicatesTitle') : t('parcels.approve.title')}
      description={
        needsConfirm
          ? t('parcels.approve.duplicatesDescription', { count: needsConfirm.duplicate_count ?? 0 })
          : t('parcels.approve.confirmDescription', { name: parcel.name })
      }
      size="md"
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => {
              setNeedsConfirm(null);
              onClose();
            }}
            disabled={approve.isPending}
          >
            {t('parcels.approve.cancel')}
          </Button>
          <Button
            variant={needsConfirm ? 'danger' : 'success'}
            loading={approve.isPending}
            onClick={() => approve.mutate({ id: parcel.id, confirm: Boolean(needsConfirm) })}
          >
            {needsConfirm ? t('parcels.approve.continue') : t('parcels.approve.confirm')}
          </Button>
        </>
      }
    >
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-ink-soft">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
          <span className="text-ink-muted">{t('parcels.approve.trekId')}</span>
          <span className="font-medium">{parcel.trek_id}</span>
          <span className="text-ink-muted">{t('parcels.approve.pinfl')}</span>
          <span className="font-medium">{parcel.pinfl}</span>
          <span className="text-ink-muted">{t('parcels.approve.totalAmount')}</span>
          <span className="font-medium">${parcel.total_amount.toFixed(2)}</span>
        </div>
      </div>
    </Dialog>
  );
};
